"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatabaseUrlParsed = exports.DatabaseUrl = void 0;
exports.parseDatabaseUrl = parseDatabaseUrl;
exports.parseDatabaseOptions = parseDatabaseOptions;
exports.formatDatabaseOptions = formatDatabaseOptions;
const zod_1 = require("zod");
const utils_1 = require("@azimutt/utils");
const database_1 = require("./database");
exports.DatabaseUrl = zod_1.z.string();
exports.DatabaseUrlParsed = zod_1.z.object({
    full: zod_1.z.string(),
    kind: database_1.DatabaseKind.optional(),
    user: zod_1.z.string().optional(),
    pass: zod_1.z.string().optional(),
    host: zod_1.z.string().optional(),
    port: zod_1.z.number().optional(),
    db: zod_1.z.string().optional(),
    options: zod_1.z.record(zod_1.z.string()).optional(),
}).strict();
// vertically align regexes with variable names ^^
const bq = /^(?:jdbc:)?bigquery:\/\/(?:([^:]+):([^@]*)@)?(?:https:\/\/)?([^:/?&]+)?(?::(\d+))?(?:\/([^?]+))?(?:\?(.+))?$/;
const couchbaseRegexxxxxxxxxxx = /^couchbases?:\/\/(?:([^:]+):([^@]*)@)?([^:/?&]+)(?::(\d+))?(?:\/([^?]+))?(?:\?(.+))?$/;
const mariadbRegexxxxxxx = /^(?:jdbc:)?mariadb:\/\/(?:([^:]+):([^@]*)@)?([^:/?&]+)(?::(\d+))?(?:\/([^?]+))?(?:\?(.+))?$/;
const mongoRegexxxxxxxxxx = /^mongodb(?:\+srv)?:\/\/(?:([^:]+):([^@]*)@)?([^:/?&]+)(?::(\d+))?(?:\/([^?]+))?(?:\?(.+))?$/;
const mysqlRegexxxxxxxxxxx = /^(?:jdbc:)?mysql:\/\/(?:([^:]+):([^@]*)@)?([^:/?&]+)(?::(\d+))?(?:\/([^?]+))?(?:\?(.+))?$/;
const oracleRegexxxxxx = /^(?:jdbc:)?oracle:thin:(?:([^\/]+)\/([^@]+)@)?(?:\/\/)?([^:\/]+)(?::(\d+))?(?:[\/:]([^?]+))?(?:\?(.+))?$/;
const postgresRe = /^(?:jdbc:)?postgres(?:ql)?:\/\/(?:([^:]+):([^@]*)@)?([^:/?&]+)(?::(\d+))?(?:\/([^?]+))?(?:\?(.+))?$/;
const sqlserver = /^(?:jdbc:)?sqlserver(?:ql)?:\/\/(?:([^:]+):([^@]*)@)?([^:/?&]+)(?::(\d+))?(?:\/([^?]+))?(?:\?(.+))?$/;
const snowflakeRegexxx = /^(?:jdbc:)?snowflake:\/\/(?:([^:]+):([^@]*)@)?([^:/?&]+)(?::(\d+))?(?:\/([^?]+))?(?:\?(.+))?$/;
const snowflakeRegexxxxxxxxxxxxxx = /^https:\/\/(?:([^:]+):([^@]*)@)?(.+?(?:\.privatelink)?\.snowflakecomputing\.com)(?::(\d+))?(?:\/([^?]+))?$/;
function parseDatabaseUrl(rawUrl) {
    const url = rawUrl.trim();
    const bigqueryMatches = url.match(bq);
    if (bigqueryMatches) {
        const kind = 'bigquery';
        const [, user, pass, host, port, db, optionsStr] = bigqueryMatches;
        const { email: user2, key: pass2, project: db2, ...options } = parseDatabaseOptions(optionsStr);
        return (0, utils_1.removeUndefined)({ full: url, kind, user: user || user2, pass: pass || pass2, host, port: port ? parseInt(port) : undefined, db: db || db2, options: Object.keys(options).length > 0 ? options : undefined });
    }
    const couchbaseMatches = url.match(couchbaseRegexxxxxxxxxxx);
    if (couchbaseMatches) {
        const kind = 'couchbase';
        const [, user, pass, host, port, db, optionsStr] = couchbaseMatches;
        const options = optionsStr ? parseDatabaseOptions(optionsStr) : undefined;
        return (0, utils_1.removeUndefined)({ full: url, kind, user, pass, host, port: port ? parseInt(port) : undefined, db, options });
    }
    const mariadbMatches = url.match(mariadbRegexxxxxxx);
    if (mariadbMatches) {
        const kind = 'mariadb';
        const [, user, pass, host, port, db, optionsStr] = mariadbMatches;
        const options = optionsStr ? parseDatabaseOptions(optionsStr) : undefined;
        return (0, utils_1.removeUndefined)({ full: url, kind, user, pass, host, port: port ? parseInt(port) : undefined, db, options });
    }
    const mongodbMatches = url.match(mongoRegexxxxxxxxxx);
    if (mongodbMatches) {
        const kind = 'mongodb';
        const [, user, pass, host, port, db, optionsStr] = mongodbMatches;
        const options = optionsStr ? parseDatabaseOptions(optionsStr) : undefined;
        return (0, utils_1.removeUndefined)({ full: url, kind, user, pass, host, port: port ? parseInt(port) : undefined, db, options });
    }
    const mysqlMatches = url.match(mysqlRegexxxxxxxxxxx);
    if (mysqlMatches) {
        const kind = 'mysql';
        const [, user, pass, host, port, db, optionsStr] = mysqlMatches;
        const options = optionsStr ? parseDatabaseOptions(optionsStr) : undefined;
        return (0, utils_1.removeUndefined)({ full: url, kind, user, pass, host, port: port ? parseInt(port) : undefined, db, options });
    }
    const oracleMatches = url.match(oracleRegexxxxxx);
    if (oracleMatches) {
        const kind = 'oracle';
        const [, user, pass, host, port, db, optionsStr] = oracleMatches;
        const options = optionsStr ? parseDatabaseOptions(optionsStr) : undefined;
        return (0, utils_1.removeUndefined)({ full: url, kind, user, pass, host, port: port ? parseInt(port) : undefined, db, options });
    }
    const postgresMatches = url.match(postgresRe);
    if (postgresMatches) {
        const kind = 'postgres';
        const [, user, pass, host, port, db, optionsStr] = postgresMatches;
        const options = optionsStr ? parseDatabaseOptions(optionsStr) : undefined;
        return (0, utils_1.removeUndefined)({ full: url, kind, user, pass, host, port: port ? parseInt(port) : undefined, db, options });
    }
    const snowflakeMatches = url.match(snowflakeRegexxx) || url.match(snowflakeRegexxxxxxxxxxxxxx);
    if (snowflakeMatches) {
        const kind = 'snowflake';
        const [, user, pass, host, port, db, optionsStr] = snowflakeMatches;
        const { db: db2, user: user2, ...options } = parseDatabaseOptions(optionsStr);
        return (0, utils_1.removeUndefined)({ full: url, kind, user: user || user2, pass, host, port: port ? parseInt(port) : undefined, db: db || db2, options: Object.keys(options).length > 0 ? options : undefined });
    }
    const sqlserverMatches = url.match(sqlserver) || parseSqlServerUrl(url);
    if (sqlserverMatches) {
        const kind = 'sqlserver';
        const [, user, pass, host, port, db, optionsStr] = sqlserverMatches;
        const options = optionsStr ? parseDatabaseOptions(optionsStr) : undefined;
        return (0, utils_1.removeUndefined)({ full: url, kind, user, pass, host, port: port ? parseInt(port) : undefined, db, options });
    }
    return { full: url };
}
function parseDatabaseOptions(options) {
    return Object.fromEntries((options || '')
        .split('&')
        .filter(o => !!o)
        .map(o => o.split('=')));
}
function formatDatabaseOptions(opts) {
    return Object.entries(opts).filter(([k, _]) => k).map(([k, v]) => `${k}=${v}`).join('&') || undefined;
}
// https://www.connectionstrings.com/sql-server 🤯
function parseSqlServerUrl(url) {
    const props = Object.fromEntries(url.split(';').map(part => part.split('=')).map(([key, value]) => [key.toLowerCase(), value]));
    const { server, ['data source']: dataSource, database, ['initial catalog']: initialCatalog, ['user id']: user, password, ...other } = props;
    const hostPart = server || dataSource;
    const databasePart = database || initialCatalog;
    if (hostPart && databasePart) {
        const [host, port] = hostPart.split(',');
        const options = Object.entries(other).map(([k, v]) => `${k}=${v}`).join('&');
        return options ? [url, user, password, host, port, databasePart, options] : [url, user, password, host, port, databasePart];
    }
    return null;
}
//# sourceMappingURL=databaseUrl.js.map